import { useState, useRef, useMemo, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { geocode } from '../../services/mapbox.js';
import { useMapStore } from '../../stores/mapStore.js';
import { Button, Input, Textarea } from '../ui/index.js';
import type { Day, GeoFeature } from '../../types/index.js';

function debounce<T extends (...args: Parameters<T>) => void>(fn: T, ms: number): T {
  let t: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); }) as T;
}

interface DayFormProps {
  projectId: string;
  day?: Day;
  onClose: () => void;
}

export function DayForm({ projectId, day, onClose }: DayFormProps) {
  const qc = useQueryClient();
  const { viewport } = useMapStore();
  const [name, setName] = useState(day?.name ?? '');
  const [shootDate, setShootDate] = useState(day?.shootDate ?? '');
  const [notes, setNotes] = useState(day?.notes ?? '');

  // Basecamp geocode state
  const [bcQuery, setBcQuery] = useState(day?.basecampAddress ?? '');
  const [bcResults, setBcResults] = useState<GeoFeature[]>([]);
  const [bcSelected, setBcSelected] = useState<{ name: string; address: string; lat: number; lng: number } | null>(
    day?.basecampAddress ? { name: day.basecampName, address: day.basecampAddress, lat: day.basecampLat ?? 0, lng: day.basecampLng ?? 0 } : null,
  );
  const proximityRef = useRef(`${viewport.lng},${viewport.lat}`);

  const searchBc = useMemo(() =>
    debounce(async (q: string) => {
      if (q.length < 2) { setBcResults([]); return; }
      const feats = await geocode(q, proximityRef.current);
      setBcResults(feats);
    }, 400),
  []); // eslint-disable-line

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name, shootDate: shootDate || undefined, notes,
        basecampName: bcSelected?.name ?? '',
        basecampAddress: bcSelected?.address ?? '',
        basecampLat: bcSelected?.lat ?? null,
        basecampLng: bcSelected?.lng ?? null,
      };
      if (day) return api.put(`/days/${day.id}`, payload);
      return api.post('/days', { projectId, ...payload });
    },
    onSuccess: (result) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['days', projectId] });
      toast.success(day ? 'Day updated' : 'Day created');
      onClose();
    },
  });

  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
      <Input label="Day name" value={name} onChange={e => setName(e.target.value)} placeholder="Day 1 — Exteriors" required />
      <Input label="Shoot date" type="date" value={shootDate} onChange={e => setShootDate(e.target.value)} />
      <Textarea label="Notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Call time, weather contingencies…" />

      {/* Basecamp */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Basecamp</label>
        {bcSelected ? (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="text-base">🏕️</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{bcSelected.name}</p>
              <p className="text-xs text-gray-500 truncate">{bcSelected.address}</p>
            </div>
            <button type="button" onClick={() => { setBcSelected(null); setBcQuery(''); }} className="text-gray-400 hover:text-red-500 text-xs">✕</button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={bcQuery}
              onChange={e => { setBcQuery(e.target.value); searchBc(e.target.value); }}
              placeholder="Search basecamp address…"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-400"
            />
            {bcResults.length > 0 && (
              <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto divide-y divide-gray-50">
                {bcResults.map(f => (
                  <li
                    key={f.id}
                    className="px-3 py-2 hover:bg-brand-50 cursor-pointer"
                    onClick={() => {
                      setBcSelected({ name: f.place_name.split(',')[0], address: f.place_name, lat: f.center[1], lng: f.center[0] });
                      setBcQuery(f.place_name);
                      setBcResults([]);
                    }}
                  >
                    <p className="text-sm text-gray-800 truncate">{f.place_name}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" loading={mutation.isPending} className="flex-1">{day ? 'Save' : 'Create day'}</Button>
        <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
      </div>
    </form>
  );
}
